import React, { useEffect, useState } from "react";
import { Button, Form, Image, Spinner } from "react-bootstrap";
import {
  LuArrowUpDown,
  LuImageOff,
  LuPencil,
  LuTrash2,
} from "react-icons/lu";
import { toast } from "react-toastify";
import DataTable, { DataTableColumn } from "../DataTable/DataTable";
import DeleteConfirmationModal from "../../Pages/ViewProduct/components/DeleteConfirmationModal";
import {
  DeleteStoreCarousel,
  GetStoreCarouselList,
  GetStoreCarouselListForStore,
  ReorderStoreCarousel,
  UpdateStoreCarousel,
} from "../../Api/apis/carouselApi";
import {
  StoreCarouselData,
  StoreCarouselImageType,
} from "../../Api/apis/types/carousel.types";
import { STORAGE_CONSTANTS } from "../../shared/Constants/Constants";
import { getCarouselImageUrl } from "./StoreCarousel";
import styles from "./StoreCarouselList.style";

const TYPE_LABELS: Record<StoreCarouselImageType, string> = {
  [StoreCarouselImageType.CAROUSEL_IMAGE]: "Image",
  [StoreCarouselImageType.PRODUCT_IMAGE]: "Product",
};

const byOrdering = (a: StoreCarouselData, b: StoreCarouselData) =>
  a.ordering - b.ordering;

const idsOf = (list: StoreCarouselData[]) => list.map((c) => c.id).join(",");

interface StoreCarouselListProps {
  onEdit?: (carousel: StoreCarouselData) => void;
  /** Lets the parent disable actions that would leave the list mid-reorder */
  onReorderingChange?: (reordering: boolean) => void;
}

const StoreCarouselList: React.FC<StoreCarouselListProps> = ({
  onEdit,
  onReorderingChange,
}) => {

  const [carousels, setCarousels] = useState<StoreCarouselData[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingIds, setTogglingIds] = useState<number[]>([]);

  const [reordering, setReordering] = useState(false);
  const [orderBeforeReorder, setOrderBeforeReorder] = useState<
    StoreCarouselData[]
  >([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<StoreCarouselData | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const fetchCarousels = async () => {
    setLoading(true);
    try {
      const response = await GetStoreCarouselListForStore();
      setCarousels([...(response.data ?? [])].sort(byOrdering));
    } catch (error) {
      console.log("Error fetching carousels", error);
      toast.error("Failed to load carousels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarousels();
  }, []);

  useEffect(() => {
    onReorderingChange?.(reordering);
  }, [reordering]);

  /** Flips the switch right away and rolls it back if the PATCH fails */
  const handleToggleActive = async (carousel: StoreCarouselData) => {
    const isActive = !carousel.is_active;
    const setActive = (value: boolean) =>
      setCarousels((list) =>
        list.map((c) => (c.id === carousel.id ? { ...c, is_active: value } : c))
      );

    setActive(isActive);
    setTogglingIds((ids) => [...ids, carousel.id]);
    try {
      await UpdateStoreCarousel(carousel.id, { is_active: isActive });
    } catch (error) {
      console.log("Error updating carousel", error);
      setActive(!isActive);
      toast.error("Couldn't update the carousel");
    } finally {
      setTogglingIds((ids) => ids.filter((id) => id !== carousel.id));
    }
  };

  const startReorder = () => {
    setOrderBeforeReorder(carousels);
    setReordering(true);
  };

  const cancelReorder = () => {
    setCarousels(orderBeforeReorder);
    setReordering(false);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await ReorderStoreCarousel({ ordered_ids: carousels.map((c) => c.id) });
      setCarousels((list) => list.map((c, i) => ({ ...c, ordering: i })));
      setReordering(false);
      toast.success("Carousel order saved");
    } catch (error) {
      console.log("Error reordering carousels", error);
      toast.error("Couldn't save the new order");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await DeleteStoreCarousel(deleteTarget.id);
      setCarousels((list) => list.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Carousel deleted");
    } catch (error) {
      console.log("Error deleting carousel", error);
      toast.error("Couldn't delete the carousel");
    } finally {
      setDeleting(false);
    }
  };

  const orderChanged = idsOf(carousels) !== idsOf(orderBeforeReorder);

  const columns: DataTableColumn<StoreCarouselData>[] = [
    {
      key: "title",
      header: "Title",
      render: (c) => <span style={styles.title}>{c.title || "-"}</span>,
    },
    {
      key: "type",
      header: "Type",
      width: 110,
      render: (c) =>
        c.carousel_image_type ? (
          <span className="badge rounded-2 fw-normal" style={styles.typeBadge}>
            {TYPE_LABELS[c.carousel_image_type]}
          </span>
        ) : (
          <span className="text-secondary">-</span>
        ),
    },
    {
      key: "preview",
      header: "Preview",
      width: 130,
      render: (c) => {
        const src = getCarouselImageUrl(c);
        return src ? (
          <Image src={src} alt={c.title} rounded style={styles.thumb} />
        ) : (
          <span
            className="d-inline-flex align-items-center justify-content-center rounded bg-light text-secondary"
            style={styles.thumb}
            title="No image"
          >
            <LuImageOff size={14} />
          </span>
        );
      },
    },
    {
      key: "is_active",
      header: "Is active",
      width: 100,
      render: (c) => (
        <Form.Check
          type="switch"
          id={`carousel-active-${c.id}`}
          className="form-switch-success mb-0"
          aria-label={`Show ${c.title || "carousel"} on storefront`}
          checked={c.is_active}
          disabled={reordering || togglingIds.includes(c.id)}
          onChange={() => handleToggleActive(c)}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: 90,
      align: "end",
      render: (c) => (
        <div className="d-inline-flex gap-1">
          <Button
            variant="link"
            size="sm"
            className="p-1"
            style={styles.iconButton}
            aria-label={`Edit ${c.title || "carousel"}`}
            disabled={!onEdit || reordering}
            onClick={() => onEdit?.(c)}
          >
            <LuPencil size={15} />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-1"
            style={styles.iconButton}
            aria-label={`Delete ${c.title || "carousel"}`}
            disabled={reordering}
            onClick={() => setDeleteTarget(c)}
          >
            <LuTrash2 size={15} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* List toolbar */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <span className="text-uppercase" style={styles.sectionLabel}>
          All carousels ({carousels.length})
        </span>
        {reordering ? (
          <div className="d-flex align-items-center gap-2">
            <span className="text-secondary small">
              Drag rows to change the order
            </span>
            <Button
              variant="light"
              size="sm"
              className="border"
              disabled={savingOrder}
              onClick={cancelReorder}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={savingOrder || !orderChanged}
              onClick={saveOrder}
            >
              {savingOrder ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Save order"
              )}
            </Button>
          </div>
        ) : (
          <Button
            variant="light"
            size="sm"
            className="border d-flex align-items-center gap-1"
            disabled={loading || carousels.length < 2}
            onClick={startReorder}
          >
            <LuArrowUpDown size={14} /> Reorder
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={carousels}
        rowKey={(c) => c.id}
        loading={loading}
        emptyMessage="No carousels yet. Add one to show it on your storefront."
        sortable={reordering}
        onReorder={setCarousels}
      />

      <DeleteConfirmationModal
        showDeleteModal={deleteTarget !== null}
        handleCloseDeleteModal={() => setDeleteTarget(null)}
        handleDelete={handleDelete}
        deleting={deleting}
      />
    </>
  );
};

export default StoreCarouselList;
